import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Plus, Trash2, Globe, Building2, Save, Loader2 } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EntityList } from '@/widgets/entityList'

import {
  useAllToolTypes,
  useSaveToolType,
  useDeleteToolType,
  type ToolTypeWithCompany,
  type TToolTypeArguments,
} from '@/entities/toolType'

type TTToolTypeArgumentsView = 'fixed' | 'text' | 'checkbox' | 'number' | 'list_key_value' | 'choice'

const ARG_TYPES: { value: TTToolTypeArgumentsView; label: string }[] = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'text', label: 'Text' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'choice', label: 'Choice (dropdown)' },
  { value: 'number', label: 'Number' },
  { value: 'list_key_value', label: 'List Key-Value' },
]

function emptyArg(): TToolTypeArguments {
  return { name: '', is_required: false, type: undefined, description: '', placeholder: '', default_value: '', allowed_value_types: [] }
}

function emptyToolType(): ToolTypeWithCompany {
  return { id: 0, type_name: '', url: null, required_arguments: [], icon_url: null, instructions: null, allow_args: false, company_id: null }
}

function ArgumentCard({
  arg,
  index,
  onChange,
  onDelete,
}: {
  arg: TToolTypeArguments
  index: number
  onChange: (index: number, updated: TToolTypeArguments) => void
  onDelete: (index: number) => void
}) {
  const update = (patch: Partial<TToolTypeArguments>) => onChange(index, { ...arg, ...patch })

  const allTypes: ('fixed_value' | 'llm_value')[] = ['fixed_value', 'llm_value']
  const effective = arg.allowed_value_types?.length ? arg.allowed_value_types : allTypes
  const hasValueType = (vt: 'fixed_value' | 'llm_value') => effective.includes(vt)

  const toggleValueType = (vt: 'fixed_value' | 'llm_value') => {
    const next = hasValueType(vt) ? effective.filter((v) => v !== vt) : [...effective, vt]
    update({ allowed_value_types: next.length === allTypes.length ? [] : next })
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-body font-medium text-gray-700">Argument {index + 1}</span>
        <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(index)} className="h-7 w-7 p-0">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-detail text-gray-500">Name</label>
          <Input value={arg.name} onChange={(e) => update({ name: e.target.value })} placeholder="argument_name" className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <label className="text-detail text-gray-500">Type</label>
          <Select
            value={arg.type ?? '__none__'}
            onValueChange={(v) => update({ type: v === '__none__' ? undefined : (v as TTToolTypeArgumentsView) })}
          >
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="(none)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">(none)</SelectItem>
              {ARG_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-detail text-gray-500">Description</label>
        <Input value={arg.description ?? ''} onChange={(e) => update({ description: e.target.value })} placeholder="What this argument does" className="h-8 text-sm" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-detail text-gray-500">Placeholder</label>
          <Input value={arg.placeholder ?? ''} onChange={(e) => update({ placeholder: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <label className="text-detail text-gray-500">Default Value</label>
          <Input value={String(arg.default_value ?? '')} onChange={(e) => update({ default_value: e.target.value })} className="h-8 text-sm" />
        </div>
      </div>

      {arg.type === 'choice' && (
        <div className="space-y-1">
          <label className="text-detail text-gray-500">Options (one per line)</label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] resize-y"
            value={(arg.options ?? []).join('\n')}
            onChange={(e) => {
              const opts = e.target.value.split('\n').filter((s) => s.trim() !== '')
              update({ options: opts })
            }}
            placeholder={"Bearer Token\nAPI Key\nBasic Auth"}
            rows={3}
          />
        </div>
      )}

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2">
          <Checkbox checked={arg.is_required} onCheckedChange={(c) => update({ is_required: Boolean(c) })} />
          <span className="text-detail text-gray-600">Required</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox checked={hasValueType('fixed_value')} onCheckedChange={() => toggleValueType('fixed_value')} />
          <span className="text-detail text-gray-600">Fixed Value</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox checked={hasValueType('llm_value')} onCheckedChange={() => toggleValueType('llm_value')} />
          <span className="text-detail text-gray-600">LLM Prompt</span>
        </label>
      </div>
    </Card>
  )
}

function ToolTypeEditor({
  toolType,
  isNew,
  onSaved,
  onDeleted,
}: {
  toolType: ToolTypeWithCompany
  isNew: boolean
  onSaved: (updated: ToolTypeWithCompany) => void
  onDeleted: (id: number) => void
}) {
  const saveToolType = useSaveToolType()
  const deleteToolType = useDeleteToolType()

  const [form, setForm] = useState<ToolTypeWithCompany>(toolType)
  const [args, setArgs] = useState<TToolTypeArguments[]>(
    () => Array.isArray(toolType.required_arguments) ? toolType.required_arguments : []
  )

  const patch = (updates: Partial<ToolTypeWithCompany>) => setForm((prev) => ({ ...prev, ...updates }))

  const handleArgChange = useCallback((index: number, updated: TToolTypeArguments) => {
    setArgs((prev) => prev.map((a, i) => (i === index ? updated : a)))
  }, [])

  const handleArgDelete = useCallback((index: number) => {
    setArgs((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = async () => {
    if (!form.type_name.trim()) {
      toast.error('Type name is required')
      return
    }

    const payload = {
      type_name: form.type_name,
      url: form.url || null,
      instructions: form.instructions || null,
      icon_url: form.icon_url || null,
      allow_args: form.allow_args,
      company_id: form.company_id,
      required_arguments: args,
    }

    saveToolType.mutate(
      { id: isNew ? undefined : form.id, data: payload },
      {
        onSuccess: (data) => {
          toast.success(isNew ? 'Tool type created' : 'Tool type updated')
          onSaved(data)
        },
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleDelete = () => {
    if (!window.confirm('Are you sure you want to delete this tool type? This cannot be undone.')) return

    deleteToolType.mutate(form.id, {
      onSuccess: () => {
        toast.success('Tool type deleted')
        onDeleted(form.id)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-y-auto">
      <div className="p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-heading font-semibold text-gray-900">
            {isNew ? 'New Tool Type' : `Edit: ${toolType.type_name}`}
          </h2>
          <div className="flex gap-2">
            {!isNew && (
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteToolType.isPending}>
                {deleteToolType.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saveToolType.isPending}>
              {saveToolType.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              {isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-body text-gray-700">Type Name</label>
            <Input value={form.type_name} onChange={(e) => patch({ type_name: e.target.value })} placeholder="e.g. transfer_call" />
          </div>
          <div className="space-y-1">
            <label className="text-body text-gray-700">URL</label>
            <Input value={form.url ?? ''} onChange={(e) => patch({ url: e.target.value || null })} placeholder="/webhook/path" />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-body text-gray-700">Instructions</label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
            value={form.instructions ?? ''}
            onChange={(e) => patch({ instructions: e.target.value || null })}
            placeholder="Instructions for the AI agent when using this tool"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-body text-gray-700">Icon URL</label>
            <Input value={form.icon_url ?? ''} onChange={(e) => patch({ icon_url: e.target.value || null })} placeholder="https://..." />
          </div>
          <div className="space-y-1">
            <label className="text-body text-gray-700">Company ID</label>
            <Input
              value={form.company_id != null ? String(form.company_id) : ''}
              onChange={(e) => {
                const val = e.target.value.trim()
                patch({ company_id: val ? Number(val) : null })
              }}
              placeholder="null = global"
            />
            <p className="text-detail text-gray-400">Leave empty for global tool type</p>
          </div>
        </div>

        <label className="flex items-center gap-2">
          <Checkbox checked={form.allow_args} onCheckedChange={(c) => patch({ allow_args: Boolean(c) })} />
          <span className="text-body text-gray-700">Allow Arguments</span>
        </label>

        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-body font-semibold text-gray-800">Arguments ({args.length})</h3>
            <Button type="button" variant="outline" size="sm" onClick={() => setArgs((prev) => [...prev, emptyArg()])}>
              <Plus className="h-4 w-4 mr-1" /> Add Argument
            </Button>
          </div>

          {args.length === 0 && (
            <p className="text-detail text-gray-400 py-2">No arguments defined. Click "Add Argument" to create one.</p>
          )}

          <div className="space-y-3">
            {args.map((arg, i) => (
              <ArgumentCard key={i} arg={arg} index={i} onChange={handleArgChange} onDelete={handleArgDelete} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ToolTypesAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: toolTypes = [], isLoading } = useAllToolTypes()
  const deleteToolType = useDeleteToolType()

  const idParam = searchParams.get('tool_type_id')
  const isNew = idParam === 'new'
  const selectedId = isNew ? 'new' : idParam ? parseInt(idParam, 10) : null

  const selected =
    isNew
      ? emptyToolType()
      : toolTypes.find((t) => t.id === selectedId) ?? null

  const setSelected = useCallback((id: number | 'new' | null) => {
    const next = new URLSearchParams(searchParams)
    if (id === null) {
      next.delete('tool_type_id')
    } else {
      next.set('tool_type_id', String(id))
    }
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const handleSaved = useCallback((updated: ToolTypeWithCompany) => {
    setSelected(updated.id)
  }, [setSelected])

  const handleDeleted = useCallback(() => {
    setSelected(null)
  }, [setSelected])

  const handleSelectItem = useCallback((item: ToolTypeWithCompany) => {
    setSelected(item.id)
  }, [setSelected])

  const handleDeleteItem = useCallback((item: ToolTypeWithCompany) => {
    if (!window.confirm('Are you sure you want to delete this tool type? This cannot be undone.')) return

    deleteToolType.mutate(item.id, {
      onSuccess: () => {
        toast.success('Tool type deleted')
        if (selectedId === item.id) {
          setSelected(null)
        }
      },
      onError: (e) => toast.error(e.message),
    })
  }, [deleteToolType, selectedId, setSelected])

  const handleCreateNew = useCallback(() => {
    setSelected('new')
  }, [setSelected])

  const createButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCreateNew}
      className="w-full"
    >
      <Plus className="w-3 h-3 mr-2" />
      Create New Tool Type
    </Button>
  )

  return (
    <div className="w-full h-full p-6 overflow-hidden flex flex-col">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-heading font-bold text-gray-900 mb-1">Tool Types</h1>
        <p className="text-detail text-gray-600">
          Manage tool type definitions across all companies
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <EntityList<ToolTypeWithCompany>
          items={toolTypes}
          selectedId={selectedId ?? undefined}
          getId={(t) => t.id}
          onSelect={handleSelectItem}
          onDelete={handleDeleteItem}
          renderPrimary={(t) => t.type_name.replace(/_/g, ' ') || 'Unnamed'}
          renderSecondary={(t) => (
            <div className="flex items-center gap-1.5 mt-0.5">
              {t.company_id == null ? (
                <Globe className="h-3 w-3 text-gray-400 flex-shrink-0" />
              ) : (
                <Building2 className="h-3 w-3 text-blue-400 flex-shrink-0" />
              )}
              <span className="text-detail text-gray-500 truncate">
                {t.company_id == null ? 'Global' : `Company ${t.company_id}`}
              </span>
            </div>
          )}
          searchPlaceholder="Search tool types..."
          searchFilter={(t, q) =>
            t.type_name.toLowerCase().includes(q) ||
            t.id.toString().includes(q)
          }
          isLoading={isLoading}
          emptyMessage="No tool types found"
          createButton={createButton}
        />

        <div className="flex-1 overflow-y-auto">
          {selected ? (
            <ToolTypeEditor
              key={isNew ? 'new' : selected.id}
              toolType={selected}
              isNew={isNew}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
            />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <p className="text-body">Select a tool type to start editing or create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}